"use client";

import { Check, ChevronDown } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import { useId, useState } from "react";

import { Popover, PopoverContent, PopoverTrigger } from "@/components/Popover";
import { usePathname, useRouter } from "@/i18n/navigation";
import { cn } from "@/lib/utils";

import { locales, type Locale } from "@/i18n/routing";

import {
  navActionButtonClass,
  navGlassDropdownClass,
} from "../Nav/Nav.constants";
import { getLocaleLabel } from "./LanguageSwitcher.constants";
import { LocaleFlag } from "./LocaleFlag";
import type { LanguageSwitcherProps } from "./LanguageSwitcher.types";

const compactTriggerClassName = cn(
  "inline-flex h-9 shrink-0 items-center gap-1.5 rounded-lg px-2.5 text-sm font-medium transition-colors",
  navActionButtonClass,
);

const footerTriggerClassName =
  "inline-flex h-10 w-full items-center justify-between gap-2 rounded-lg border border-zinc-200/50 bg-muted/30 px-3 text-sm transition-colors hover:bg-muted/50 dark:border-white/[0.05] dark:bg-white/[0.04] dark:hover:bg-white/[0.06]";

const popupClassName = (isFooter: boolean) =>
  cn(
    "w-[var(--anchor-width)] max-w-[var(--anchor-width)] gap-0 overflow-hidden p-1 shadow-lg ring-0",
    isFooter
      ? "rounded-lg border border-zinc-200/50 dark:border-white/[0.05] bg-background"
      : cn(
          navGlassDropdownClass,
          "rounded-xl border-white/40 p-1 dark:border-white/[0.05]",
        ),
  );

const optionClassName = (isFooter: boolean, isSelected: boolean) =>
  cn(
    "flex w-full items-center gap-2 rounded-md text-left text-sm transition-colors",
    isFooter ? "px-3 py-2" : "px-2 py-1.5",
    isSelected
      ? isFooter
        ? "bg-muted/40 font-medium text-foreground"
        : "bg-white/55 font-medium text-foreground dark:bg-white/10 dark:text-white"
      : cn(
          "text-muted-foreground hover:bg-muted/60",
          !isFooter &&
            "dark:text-white/60 dark:hover:bg-white/10 dark:hover:text-white",
        ),
  );

export const LanguageSwitcher = ({
  variant = "compact",
}: LanguageSwitcherProps) => {
  const t = useTranslations();
  const locale = useLocale() as Locale;
  const pathname = usePathname();
  const router = useRouter();
  const labelId = useId();
  const listboxId = useId();
  const [open, setOpen] = useState(false);

  const label =
    variant === "footer" ? t("footer.language") : t("nav.a11y.language");
  const isFooter = variant === "footer";
  const includeName = isFooter;

  const handleChange = (nextLocale: Locale) => {
    setOpen(false);
    if (nextLocale !== locale) {
      router.replace(pathname, { locale: nextLocale });
      router.refresh();
    }
  };

  return (
    <div
      className={cn(
        "flex gap-2",
        isFooter
          ? "w-full flex-col items-stretch text-sm text-muted-foreground"
          : "items-center",
      )}
    >
      <label
        id={labelId}
        className={cn(
          isFooter
            ? "shrink-0 text-[11px] font-medium uppercase tracking-[0.24em] text-muted-foreground"
            : "sr-only",
        )}
      >
        {label}
      </label>
      <Popover open={open} onOpenChange={setOpen} modal={false}>
        <PopoverTrigger
          aria-label={label}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={listboxId}
          className={
            isFooter ? footerTriggerClassName : compactTriggerClassName
          }
        >
          <LocaleFlag locale={locale} />
          <span className="truncate">
            {getLocaleLabel(locale, includeName)}
          </span>
          <ChevronDown
            className={cn(
              "size-3.5 shrink-0 opacity-70 transition-transform",
              open && "rotate-180",
            )}
            aria-hidden
          />
        </PopoverTrigger>
        <PopoverContent
          side={isFooter ? "top" : "bottom"}
          align="end"
          sideOffset={8}
          collisionPadding={12}
          role="presentation"
          initialFocus={false}
          className={popupClassName(isFooter)}
        >
          <ul id={listboxId} role="listbox" aria-labelledby={labelId}>
            {locales.map((item) => {
              const isSelected = item === locale;

              return (
                <li key={item} role="presentation">
                  <button
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    onClick={() => handleChange(item)}
                    className={optionClassName(isFooter, isSelected)}
                  >
                    <LocaleFlag locale={item} />
                    <span className="min-w-0 flex-1 truncate">
                      {getLocaleLabel(item, includeName)}
                    </span>
                    {isFooter ? (
                      <Check
                        className={cn(
                          "size-4 shrink-0 text-brand",
                          isSelected ? "opacity-100" : "opacity-0",
                        )}
                        aria-hidden
                      />
                    ) : null}
                  </button>
                </li>
              );
            })}
          </ul>
        </PopoverContent>
      </Popover>
    </div>
  );
};
